
interface SettingsProps {
  onClose: () => void;
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
  soundEnabled: boolean;
  setSoundEnabled: (value: boolean) => void;
}

export default function Settings({ onClose, darkMode, setDarkMode, soundEnabled, setSoundEnabled }: SettingsProps) {
  const handleDarkModeToggle = (value: boolean) => {
    setDarkMode(value);
    localStorage.setItem('wordleDarkMode', JSON.stringify(value));
  };

  const handleSoundToggle = (value: boolean) => {
    setSoundEnabled(value);
    localStorage.setItem('wordleSoundEnabled', JSON.stringify(value));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl max-w-md w-full p-6 relative animate-scale-in`}>
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg ${
            darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
          } transition-colors cursor-pointer`}
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        <h2 className={`text-2xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Settings</h2>

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Dark Mode</h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Switch between light and dark themes
              </p>
            </div>
            <button
              onClick={() => handleDarkModeToggle(!darkMode)}
              className={`relative w-14 h-8 rounded-full transition-colors cursor-pointer ${
                darkMode ? 'bg-teal-500' : 'bg-gray-300'
              }`}
            >
              <div
                className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                  darkMode ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Sound Effects</h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Play sounds for correct/wrong guesses
              </p>
            </div>
            <button
              onClick={() => handleSoundToggle(!soundEnabled)}
              className={`relative w-14 h-8 rounded-full transition-colors cursor-pointer ${
                soundEnabled ? 'bg-teal-500' : darkMode ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <div
                className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                  soundEnabled ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className={`pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>How to Play</h3>
            <ul className={`space-y-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <li>• Guess the word in 6 tries</li>
              <li>• Each guess must be a valid 5-letter word</li>
              <li>• The color of the tiles will change to show how close your guess was</li>
              <li className="flex items-center gap-2 mt-3">
                <span className="w-8 h-8 bg-teal-500 rounded flex items-center justify-center text-white font-bold">W</span>
                <span>Letter is in the word and in the correct spot</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-500 rounded flex items-center justify-center text-white font-bold">O</span>
                <span>Letter is in the word but in the wrong spot</span>
              </li>
              <li className="flex items-center gap-2">
                <span className={`w-8 h-8 ${darkMode ? 'bg-gray-700' : 'bg-gray-400'} rounded flex items-center justify-center text-white font-bold`}>R</span>
                <span>Letter is not in the word</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
